import type { Landmark } from '../../../data/types';
import type { Ring, Vec2 } from '../../../geo/types';
import type { LinearRGB } from '../../../core/color';
import type { Frame, MeshBuilder } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import {
  boxSolid,
  horizontalCap,
  orientedFace,
  planPoint,
  prismSolid,
  subFrame,
  triangulateRing,
} from '../solids';
import { gableRoof } from '../roofSolids';
import { corners, type PlanRect } from './genericBlock';
import { edgePanel, openedWall, rectWall, type MasonryStyles } from './masonry';
import { paintedChapelStyles, type PaintedChapelColors } from './paintedChapels';
import { meshFrameOf, toLayout, type PlanFrame } from './planFrame';
import { NO_TRACERY } from './tracery/tracerySink';
import { transverseRoof } from './gothic/roofs';
import readings from './agnietenCourtPlan.json';

/** Individual masses read in the painting/woodcut; metre dimensions are fitted estimates. */
export const AGNIETEN_COURT = readings;
type Range = (typeof readings.ranges)[number];

export function agnietenCourtFrame(landmark: Landmark): PlanFrame {
  const x = landmark.params['anchorX'],
    y = landmark.params['anchorY'];
  if (typeof x !== 'number' || typeof y !== 'number')
    throw new Error('Agnieten court lacks its chapel anchor');
  return { origin: [x, y], heading: landmark.heading, ring: [] };
}

/** The front passage and whole court remain walkable. No fictitious church occupies them. */
export function agnietenConventObstacles(landmark: Landmark): Ring[] {
  const plan = agnietenCourtFrame(landmark);
  return readings.ranges.map((r) => corners(r).map((p) => toLayout(plan, p)));
}

function turned(r: PlanRect): PlanRect {
  return { u0: r.v0, u1: r.v1, v0: -r.u1, v1: -r.u0 };
}

function frontRoof(
  builder: MeshBuilder,
  frame: Frame,
  styles: MasonryStyles,
  r: Range,
  rect: PlanRect
): void {
  const vc = (rect.v0 + rect.v1) / 2;
  const dormers = readings.frontDormers.filter((v) => v > r.v0 && v < r.v1);
  const holes = dormers.map((u) => corners({ u0: u - 0.9, u1: u + 0.9, v0: 11.8, v1: 13.9 }));
  for (const sign of [-1, 1]) {
    const edge = sign < 0 ? rect.v0 : rect.v1;
    const slope = corners({
      u0: rect.u0,
      u1: rect.u1,
      v0: Math.min(edge, vc),
      v1: Math.max(edge, vc),
    });
    for (const tri of triangulateRing(slope, sign < 0 ? holes : []))
      orientedFace(
        builder,
        styles.roof,
        frame,
        tri.map(([u, v]) =>
          planPoint(u, r.eaves + ((r.ridge - r.eaves) * (v - edge)) / (vc - edge), v)
        ),
        [0, 1, -sign]
      );
  }
}

/** Trim the dormer's walls to the roof plane, sharing the four cut edges with its slope. */
function frontDormer(builder: MeshBuilder, frame: Frame, styles: MasonryStyles, u: number): void {
  const u0 = u - 0.9,
    u1 = u + 0.9,
    v0 = 11.8,
    v1 = 13.9;
  const roofAt = (v: number) => 3.2 + (1.9 * (v - 11.6)) / 3.35;
  for (const back of [false, true]) {
    const v = back ? v1 : v0,
      base = roofAt(v);
    const edge: readonly [Vec2, Vec2] = back
      ? [
          [u1, v],
          [u0, v],
        ]
      : [
          [u0, v],
          [u1, v],
        ];
    openedWall(
      builder,
      styles,
      frame,
      edgePanel(...edge),
      [
        [0, base],
        [1.8, base],
        [1.8, 4.8],
        [0.9, 6.2],
        [0, 4.8],
      ],
      back
        ? []
        : [
            [
              [0.45, 3.6],
              [1.35, 3.6],
              [1.35, 4.55],
              [0.45, 4.55],
            ],
          ]
    );
  }
  for (const sign of [-1, 1]) {
    const x = sign < 0 ? u0 : u1;
    orientedFace(
      builder,
      styles.wall,
      frame,
      [
        planPoint(x, roofAt(v0), v0),
        planPoint(x, roofAt(v1), v1),
        planPoint(x, 4.8, v1),
        planPoint(x, 4.8, v0),
      ],
      [sign, 0, 0]
    );
  }
  transverseRoof(builder, styles, frame, { u0, u1, v0, v1 }, { eaveY: 4.8, ridgeY: 6.2 });
}

function range(builder: MeshBuilder, frame: Frame, styles: MasonryStyles, r: Range): void {
  const f = r.alongU ? frame : { ...frame, yaw: frame.yaw + Math.PI / 2 };
  const rect = r.alongU ? r : turned(r),
    ring = corners(rect);
  ring.forEach((from, side) => {
    const to = ring[(side + 1) % 4]!,
      end = side % 2 === 1;
    const length = Math.hypot(to[0] - from[0], to[1] - from[1]);
    const bays = Math.max(1, Math.floor(length / 3.2));
    const touching = readings.ranges
      .filter((other) => other !== r)
      .flatMap((other) => {
        const q = r.alongU ? other : { ...turned(other), eaves: other.eaves };
        let span: [number, number] | null = null;
        if (side === 0 && q.v1 === rect.v0)
          span = [Math.max(q.u0, rect.u0) - rect.u0, Math.min(q.u1, rect.u1) - rect.u0];
        if (side === 2 && q.v0 === rect.v1)
          span = [rect.u1 - Math.min(q.u1, rect.u1), rect.u1 - Math.max(q.u0, rect.u0)];
        if (side === 1 && q.u0 === rect.u1)
          span = [Math.max(q.v0, rect.v0) - rect.v0, Math.min(q.v1, rect.v1) - rect.v0];
        if (side === 3 && q.u1 === rect.u0)
          span = [rect.v1 - Math.min(q.v1, rect.v1), rect.v1 - Math.max(q.v0, rect.v0)];
        return span && span[1] > span[0] ? [{ x0: span[0], x1: span[1], height: q.eaves }] : [];
      });
    const holes: Ring[] = [];
    for (let floor = 0; floor < r.floors; floor++) {
      const sill = 1.05 + floor * 2.95;
      for (let bay = 0; bay < bays; bay++) {
        const x = ((bay + 0.5) * length) / bays;
        holes.push([
          [x - 0.42, sill],
          [x + 0.42, sill],
          [x + 0.42, sill + 1.5],
          [x - 0.42, sill + 1.5],
        ]);
      }
    }
    if (end) {
      const x = length / 2,
        y = r.eaves + 0.45;
      holes.push([
        [x - 0.25, y],
        [x + 0.25, y],
        [x + 0.25, y + 0.75],
        [x - 0.25, y + 0.75],
      ]);
      openedWall(
        builder,
        styles,
        f,
        edgePanel(from, to),
        [
          [0, r.eaves],
          [length, r.eaves],
          [length / 2, r.ridge],
        ],
        holes.filter((h) => h[0]![1] > r.eaves)
      );
    }
    const visibleHoles = holes.filter(
      (h) => !touching.some((t) => h[0]![0] < t.x1 && h[1]![0] > t.x0 && h[0]![1] < t.height)
    );
    rectWall(
      builder,
      styles,
      f,
      [from, to],
      [0, r.eaves],
      visibleHoles.filter((h) => h[2]![1] <= r.eaves),
      0
    );
    const face = subFrame(f, from[0], from[1], Math.atan2(to[1] - from[1], to[0] - from[0]));
    for (let floor = 0; floor < r.floors; floor++) {
      const y = 1.05 + floor * 2.95;
      for (let bay = 0; bay < bays; bay++) {
        const x = ((bay + 0.5) * length) / bays;
        if (touching.some((t) => x - 0.42 < t.x1 && x + 0.42 > t.x0 && y < t.height)) continue;
        boxSolid(
          builder,
          styles.glazing!.reveal,
          face,
          [x - 0.48, y - 0.1, -0.09],
          [x + 0.48, y + 0.03, 0.02],
          { top: true, bottom: true }
        );
        boxSolid(
          builder,
          styles.glazing!.reveal,
          face,
          [x - 0.025, y, -0.025],
          [x + 0.025, y + 1.5, 0.025],
          { top: true, bottom: true }
        );
      }
    }
  });
  horizontalCap(builder, styles.wall, f, ring, 0.01, true);
  if (r.id.startsWith('front-range')) frontRoof(builder, f, styles, r, rect);
  else
    gableRoof(builder, styles, f, {
      ...rect,
      eaveY: r.eaves,
      ridgeY: r.ridge,
      ends: { start: false, end: false },
    });
}

/** Unequal court ranges, paired hall gables, four small front roof gables and plain entry. */
export function buildAgnietenConvent(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: PaintedChapelColors & { ground: LinearRGB; garden: LinearRGB },
  ids: { edgeId: number; seed: number }
): void {
  const plan = agnietenCourtFrame(landmark),
    frame = meshFrameOf(plan);
  const chapel = paintedChapelStyles(colors, ids, NO_TRACERY);
  const styles = { ...chapel, wall: { ...chapel.wall, surface: SURFACE.brick } };
  for (const r of readings.ranges) range(builder, frame, styles, r);
  // The small front roof gables face the court. They are not four additional houses.
  const front = { ...frame, yaw: frame.yaw + Math.PI / 2 };
  for (const v of readings.frontDormers) frontDormer(builder, front, styles, v);
  const entry = readings.entry;
  prismSolid(builder, styles.wall, frame, corners({ ...entry, u1: entry.u0 + 0.55 }), 2.65, 3.55, {
    top: true,
    bottom: true,
  });
  // Plain paving through the entry connects directly to the public canal street.
  const ground = { ...styles.wall, color: colors.ground, surface: SURFACE.street };
  horizontalCap(builder, ground, frame, corners(entry), 0.03, true);
  const c = readings.court;
  horizontalCap(
    builder,
    { ...ground, color: colors.garden, surface: SURFACE.garden },
    frame,
    corners(c),
    0.035,
    true
  );
  const path: Vec2[] = [
    [c.u0, entry.v0],
    [c.u1, entry.v0],
    [c.u1, entry.v1],
    [c.u0, entry.v1],
  ];
  horizontalCap(builder, ground, frame, path, 0.045, true);
}
